import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { type ConversationAction } from '@atlassian/rovo-conversation-actions/types';

import { fg } from '@confluence/feature-gating';
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import { useStatusUpdatesAutomationNudgeAction } from '../status-updates/useStatusUpdatesAutomationNudgeAction.tsx';

import { useConfluenceAutomationsBaseEligibility } from './useConfluenceAutomationsBaseEligibility.ts';
import { useStudioPermissions } from './useStudioPermissions.ts';

import type {
	useConfluenceAutomationNudgeActionsQuery$data,
	useConfluenceAutomationNudgeActionsQuery$variables,
} from './__generated__/useConfluenceAutomationNudgeActionsQuery.graphql.ts';

export interface AutomationNudgeActionEntry {
	actionId: string;
	action: ConversationAction;
}

export const useConfluenceAutomationNudgeActions = (
	contentId: string,
): AutomationNudgeActionEntry[] => {
	const hasBaseEligibility = useConfluenceAutomationsBaseEligibility();

	const data = useQueryInterop<
		useConfluenceAutomationNudgeActionsQuery$data,
		useConfluenceAutomationNudgeActionsQuery$variables
	>(
		graphql`
			query useConfluenceAutomationNudgeActionsQuery(
				$contentId: ID!
				$includeStatusUpdatesAutomation: Boolean!
			) {
				user: confluence_atlassianUser(current: true) {
					__typename
					id
					...useStatusUpdatesAutomationNudgeAction_user
						@alias(as: "statusUpdatesAutomationUser")
						@include(if: $includeStatusUpdatesAutomation)
				}
				singleContent(id: $contentId) {
					__typename
					id
					...useStatusUpdatesAutomationNudgeAction_content
						@alias(as: "statusUpdatesAutomationContent")
						@include(if: $includeStatusUpdatesAutomation)
				}
			}
		`,
		{
			variables: {
				contentId,
				includeStatusUpdatesAutomation: fg('cc_automations_discovery_nudges'),
			},
			skip: !hasBaseEligibility || !contentId,
		},
	);

	const statusUpdatesAutomationNudgeAction = useStatusUpdatesAutomationNudgeAction({
		contentFragmentRef: data.data?.singleContent?.statusUpdatesAutomationContent,
		userFragmentRef: data.data?.user?.statusUpdatesAutomationUser,
	});

	const matchedActions = useMemo<AutomationNudgeActionEntry[]>(
		() =>
			[
				{ actionId: 'status-updates-automation', action: statusUpdatesAutomationNudgeAction },
			].filter((entry): entry is AutomationNudgeActionEntry => entry.action != null),
		[statusUpdatesAutomationNudgeAction],
	);

	// Defer the Studio permission check until at least one nudge has matched, so
	// the permission query never runs for the common case where nothing matches —
	// while still only running it once for all nudges.
	const { canCreateStudioAutomation } = useStudioPermissions({ skip: matchedActions.length === 0 });

	return useMemo<AutomationNudgeActionEntry[]>(
		() => (canCreateStudioAutomation ? matchedActions : []),
		[matchedActions, canCreateStudioAutomation],
	);
};
